import { supabase } from '../supabaseClient'
import { TipoTareaArt10, IluminacionFullReport } from './iluminacion'

// ===== Catálogo de luminarias (BiogavalMethodologyPage-style: catálogo global) =====

export interface LuminaireCatalogItem {
  id: string
  origen: 'storsafe_catalogo' | 'proveedor_patrocinado' | 'cliente_propio'
  manufacturer: string
  lumcat: string
  luminaire_description: string | null
  family: string
  total_lumens: number
  input_wattage: number
  num_lamps: number
  multiplier: number
  vertical_angles: number[]
  horizontal_angles: number[]
  candela_matrix: number[][]
  categoria: CategoriaLuminaria | null
  grado_ip: string | null
  clasificacion_area: string | null
  distribuidor_ecuador: string | null
  created_at: string
}

export type CategoriaLuminaria =
  'Panel' | 'Envolvente' | 'Regleta' | 'High bay' | 'Low bay' | 'Hermética' | 'Área clasificada'

// Mismo orden y valores que el CHECK de 20260928010000.
export const CATEGORIAS_LUMINARIA: CategoriaLuminaria[] =
  ['Panel', 'Envolvente', 'Regleta', 'High bay', 'Low bay', 'Hermética', 'Área clasificada']

// Texto único para pantalla y PDF.
export const AVISO_AREA_CLASIFICADA =
  'StorSafe calcula la iluminación; no certifica la aptitud de la luminaria para la zona. ' +
  'La clasificación del área y la certificación exigida (Clase/División o Zona) las define el responsable de la instalación eléctrica.'

// Distribuidor en Ecuador: dato INFORMATIVO, sin relación comercial (StorSafe
// no cobra ni recibe nada por mostrarlo). Solo en pantalla, nunca en el PDF.
export function textoDistribuidorEcuador(l: Pick<LuminaireCatalogItem, 'distribuidor_ecuador'> | null | undefined): string | null {
  return l?.distribuidor_ecuador ? `Disponible en Ecuador vía: ${l.distribuidor_ecuador}` : null
}

export async function fetchLuminaireCatalog(): Promise<LuminaireCatalogItem[]> {
  const { data, error } = await supabase
    .from('luminaire_catalog')
    .select('id, origen, manufacturer, lumcat, luminaire_description, family, total_lumens, input_wattage, num_lamps, multiplier, vertical_angles, horizontal_angles, candela_matrix, categoria, grado_ip, clasificacion_area, distribuidor_ecuador, created_at')
    .order('family')
    .order('lumcat')
  if (error) throw error
  return (data ?? []) as LuminaireCatalogItem[]
}

// ===== Cálculos de iluminación por local/puesto de trabajo (multi-empresa) =====

export interface LightingCalculationRow {
  id: string
  company_id: string
  job_position_id: string | null
  nombre_local: string
  tipo_tarea_art10: TipoTareaArt10
  inputs: Record<string, any>
  resultado: IluminacionFullReport
  cumple_normativa: boolean
  created_by: string | null
  created_at: string
  job_position_name: string | null
}

export interface CreateLightingCalculationInput {
  companyId: string
  jobPositionId?: string
  nombreLocal: string
  tipoTareaArt10: TipoTareaArt10
  inputs: Record<string, any>
  resultado: IluminacionFullReport
  cumpleNormativa: boolean
  createdBy: string | null
}

export async function createLightingCalculation(input: CreateLightingCalculationInput): Promise<void> {
  const { error } = await supabase.from('lighting_calculations').insert({
    company_id: input.companyId,
    job_position_id: input.jobPositionId ?? null,
    nombre_local: input.nombreLocal,
    tipo_tarea_art10: input.tipoTareaArt10,
    inputs: input.inputs,
    resultado: input.resultado,
    cumple_normativa: input.cumpleNormativa,
    created_by: input.createdBy,
  })
  if (error) throw error
}

export async function fetchLightingCalculations(companyId: string): Promise<LightingCalculationRow[]> {
  if (!companyId) return []
  const { data, error } = await supabase
    .from('lighting_calculations')
    .select('id, company_id, job_position_id, nombre_local, tipo_tarea_art10, inputs, resultado, cumple_normativa, created_by, created_at, job_positions(name)')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    id: r.id,
    company_id: r.company_id,
    job_position_id: r.job_position_id,
    nombre_local: r.nombre_local,
    tipo_tarea_art10: r.tipo_tarea_art10,
    inputs: r.inputs,
    resultado: r.resultado,
    cumple_normativa: r.cumple_normativa,
    created_by: r.created_by,
    created_at: r.created_at,
    job_position_name: r.job_positions?.name ?? null,
  })) as LightingCalculationRow[]
}
